import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getLearnerActivityReport } from '../../data/reportHelpers';
import { getRiskLevel, getStatusColor } from '../../data/helpers';
import SummaryStatBar from './SummaryStatBar';
import ExportBar from './ExportBar';

const { FiUsers, FiSearch, FiCheckCircle, FiAlertTriangle, FiShield } = FiIcons;

const COLUMNS = [
  { key: 'name', label: 'Learner' },
  { key: 'email', label: 'Email' },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'location', label: 'Location' },
  { key: 'trainingStatus', label: 'Training Status' },
  { key: 'riskScore', label: 'Risk Score' },
  { key: 'totalCampaigns', label: 'Campaigns Assigned' },
  { key: 'completedCampaigns', label: 'Campaigns Completed' },
  { key: 'avgCompletion', label: 'Avg Completion %' },
  { key: 'phishingClicks', label: 'Phishing Clicks' },
  { key: 'phishingReports', label: 'Phishing Reports' },
  { key: 'credentialSubmissions', label: 'Credential Submissions' },
  { key: 'securityAcks', label: 'Security Acks' },
  { key: 'policyAcks', label: 'Policy Acks' },
  { key: 'lastTrainingDate', label: 'Last Training Date' },
];

const LearnerActivityReport = ({ filters, raw }) => {
  const data = useMemo(() => getLearnerActivityReport(raw, filters), [raw, filters]);
  const [search, setSearch] = useState('');

  const filtered = data.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase())
  );

  const stats = [
    { label: 'Total Learners', value: data.length, icon: FiUsers, iconColor: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Compliant', value: data.filter(r => r.trainingStatus === 'Compliant').length, icon: FiCheckCircle, iconColor: 'bg-emerald-50 text-emerald-600', border: 'border-emerald-100' },
    { label: 'Overdue', value: data.filter(r => r.trainingStatus === 'Overdue').length, icon: FiAlertTriangle, iconColor: 'bg-red-50 text-red-600', border: 'border-red-100' },
    { label: 'Avg. Risk Score', value: Math.round(data.reduce((a, b) => a + b.riskScore, 0) / (data.length || 1)), icon: FiShield, iconColor: 'bg-violet-50 text-violet-600', border: 'border-violet-100' },
    { label: 'High Risk', value: data.filter(r => r.riskScore >= 50).length, icon: FiAlertTriangle, iconColor: 'bg-rose-50 text-rose-600', border: 'border-rose-100' },
    { label: 'Avg. Completion', value: `${Math.round(data.reduce((a, b) => a + b.avgCompletion, 0) / (data.length || 1))}%`, icon: FiCheckCircle, iconColor: 'bg-teal-50 text-teal-600', border: 'border-teal-100' },
  ];

  return (
    <div className="space-y-4">
      <SummaryStatBar stats={stats} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="relative max-w-sm">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search learner…"
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
        </div>
      </div>
      <ExportBar reportTitle="Learner Activity Report" data={filtered} columns={COLUMNS} summaryStats={stats.map(s => ({ label: s.label, value: s.value }))} rowCount={filtered.length} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Learner</th>
                <th className="p-4">Department</th>
                <th className="p-4">Risk Score</th>
                <th className="p-4">Status</th>
                <th className="p-4">Campaigns</th>
                <th className="p-4">Avg. Completion</th>
                <th className="p-4">Clicks</th>
                <th className="p-4">Reports</th>
                <th className="p-4 whitespace-nowrap">Sec. Acks</th>
                <th className="p-4 whitespace-nowrap">Pol. Acks</th>
                <th className="p-4">Last Training</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {filtered.map(row => {
                const risk = getRiskLevel(row.riskScore);
                const riskColor = row.riskScore < 20 ? '#10b981' : row.riskScore < 50 ? '#f59e0b' : '#ef4444';
                return (
                  <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {row.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{row.name}</p>
                          <p className="text-xs text-slate-400">{row.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-xs text-slate-600">{row.department}<br /><span className="text-slate-400">{row.manager}</span></td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-bold" style={{ color: riskColor }}>{row.riskScore}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold border ${risk.color}`}>{risk.label}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(row.trainingStatus)}`}>{row.trainingStatus}</span>
                    </td>
                    <td className="p-4 text-xs text-slate-600">{row.completedCampaigns}/{row.totalCampaigns} completed</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-14 bg-slate-100 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${row.avgCompletion === 100 ? 'bg-emerald-500' : row.avgCompletion >= 50 ? 'bg-blue-500' : 'bg-red-400'}`} style={{ width: `${row.avgCompletion}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600">{row.avgCompletion}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-red-600 font-semibold text-sm">{row.phishingClicks}</td>
                    <td className="p-4 text-emerald-600 font-semibold text-sm">{row.phishingReports}</td>
                    <td className="p-4 text-slate-600 font-semibold text-sm">{row.securityAcks > 0 ? row.securityAcks : '—'}</td>
                    <td className="p-4 text-slate-600 font-semibold text-sm">{row.policyAcks > 0 ? row.policyAcks : '—'}</td>
                    <td className="p-4 text-xs text-slate-500">{row.lastTrainingDate || 'Never'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">{filtered.length} of {data.length} learners</div>
      </div>
    </div>
  );
};

export default LearnerActivityReport;